/**
 * CP-484: columna opcional `descripcion` en la carga masiva de cuentas.
 *
 * La "Descripción" que ve el collector en el portal vendor se guarda en
 * `payment_accounts.data` (no en `description`, que es el "Last Update" del
 * sistema). El worker de la carga masiva (AsyncTasks) ignora esa columna, así que
 * Web Admin la aplica aparte, cuenta por cuenta, con
 * `POST /Collection/accounts/web_account {collector_id, account_number, description}`
 * — el mismo endpoint que usa el portal (CoreDB: CollectionController.PostWebAccount
 * → PostAccountProc: `if (req.description != null) acc.data = req.description`).
 *
 * Reglas (CoreDB no valida nada; se definen acá a partir de las 22.993 descripciones
 * que había en prd el 15/10: largo p99 = 68, máximo 291, 12% con tildes/ñ):
 *   - opcional: celda vacía = no se toca la descripción de esa cuenta;
 *   - hasta DESCRIPTION_MAX caracteres;
 *   - una sola línea, sin caracteres de control;
 *   - tildes/ñ permitidas (el CSV se decodifica UTF-8 o windows-1252 en el browser).
 *   `;` y `"` ya los impide el formato del archivo.
 *
 * Sin imports de runtime para que corra con `node --test`.
 */

export const DESCRIPTION_MAX = 200;

/** Nombres de columna aceptados (en minúscula). El de la plantilla es `descripcion`. */
export const DESCRIPTION_HEADERS = ["descripcion", "descripción", "description"];

export const isDescriptionHeader = (h: string) => DESCRIPTION_HEADERS.includes(h.trim().toLowerCase());

/** Normaliza (trim + espacios internos colapsados). */
export const normalizeDescription = (v: string | null | undefined) => (v ?? "").replace(/\s+/g, " ").trim();

/** null = válida (o vacía, que significa "no tocar"); si no, el motivo. */
export function descriptionError(raw: string | null | undefined): string | null {
  const v = raw ?? "";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(v.replace(/\t/g, ""))) return "Description can't contain line breaks or control characters";
  const n = normalizeDescription(v);
  if (n.length > DESCRIPTION_MAX) return `Description is too long (${n.length} chars, max ${DESCRIPTION_MAX})`;
  return null;
}

/** Estados de async_tasks: pending → in_progress → completed | error. */
export const isTaskFinished = (status: string) => /^(completed|error|failed)$/i.test(status.trim());

/**
 * La tarea de este upload: la más nueva creada desde `sinceMs` (con 2 min de margen
 * por diferencia de reloj entre el browser y CoreDB).
 */
export function pickUploadTask<T extends { createdAt: string | null }>(tasks: T[], sinceMs: number): T | null {
  const cands = tasks
    .map((t) => ({ t, ms: t.createdAt ? Date.parse(t.createdAt) : NaN }))
    .filter((x) => !Number.isNaN(x.ms) && x.ms >= sinceMs - 120_000)
    .sort((a, b) => b.ms - a.ms);
  return cands[0]?.t ?? null;
}
